import { Box, Button, Flex, Icon, SimpleGrid, Skeleton, Text } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import router from 'next/router'
import Image from 'next/image'
import { useSession } from '@/context/SessionProvider'
import { useQuery } from '@tanstack/react-query'
import { GET_ALL_USER_UNCLAIMED_REWARDS } from '@/api/query'
import { Lootbox, LootboxState, LootboxType } from './transformLootboxRewards'
import { BigNumberInWei } from '@injectivelabs/utils'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { LootboxNavigation } from '../molecules/LootboxNavigation'
import LockIcon from '@/public/assets/icons/lock.svg'
import { LootboxTags } from '../molecules/LootboxTags/LootboxTags'
import { useQuery as useAppoloQuery } from '@apollo/client'
import { GetAllUserUnclaimedRewardsQuery } from '@/__generated__/graphql'
import { useLootboxContext } from '@/context/LootboxProvider'
import { LootboxModals } from '@/context/LootboxModalProvider'

export const LootboxInventory = () => {
  const { t } = useTranslation()
  return (
    <>
      <LootboxNavigation />
      <Flex mb={6} justifyContent={'space-between'} flexWrap={{ base: 'wrap', sm: 'nowrap' }} alignItems={'center'}>
        <Text fontSize={'36px'} fontWeight={700}>
          {t('lootbox.your-inventory')}
        </Text>
        <Flex gap={2}>
          <ClaimRewards />
        </Flex>
      </Flex>
      <LootboxInventoryList />
    </>
  )
}

const ClaimRewards = () => {
  const { t } = useTranslation()
  const { account } = useSession()
  const modal = useAppModal()

  const { data, loading, error, refetch } = useAppoloQuery(GET_ALL_USER_UNCLAIMED_REWARDS, {
    variables: {
      address: account?.injectiveAddress!,
    },
    fetchPolicy: 'no-cache',
    skip: !account?.injectiveAddress,
  })

  const rewards = transformRewards(data)

  if (loading || !!error || !rewards?.length) {
    return null
  }

  return (
    <Button
      onClick={() => {
        modal.openModalNew(LootboxModals.LootboxRewardModal, {
          rewards: rewards as any,
          callback: () => {
            setTimeout(() => {
              refetch()
            }, 1_000)
          },
        })
      }}
      rounded={'2xl'}
      fontSize={{ base: 'xs', sm: 'sm' }}
      px={{ base: 4, sm: 6 }}
      variant={'orange'}
    >
      {t('lootbox.claim-rewards', { count: rewards?.length || 0 })}
    </Button>
  )
}

const transformRewards = (data?: GetAllUserUnclaimedRewardsQuery) => {
  if (!data?.lootboxGames?.nodes.length) {
    return null
  }

  return data?.lootboxGames?.nodes.map((reward) => ({
    contract: reward?.lootbox,
    id: reward?.id,
    rewardId: reward?.rewardId,
  }))
}

import WalletIcon from '@/public/assets/icons/wallet.svg'
import ChestIcon from '@/public/assets/icons/chest-filled.svg'
import Link from 'next/link'
import { WalletButton } from '@/components/atoms/WalletButton/WalletButton'
import { useAppModal } from '@/context/ModalProvider'

const LootboxInventoryList = () => {
  const { t } = useTranslation()
  const { account } = useSession()
  const { getLootboxBalances } = useLootboxContext()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['LOOTBOX_BALANCES', [account?.injectiveAddress]],
    queryFn: async () => {
      return await getLootboxBalances(account?.injectiveAddress!)
    },
    enabled: !!account?.injectiveAddress,
  })

  if (!account?.injectiveAddress) {
    return (
      <NoResultsFallback
        icon={<ChestIcon />}
        render={() => (
          <Box mt={6}>
            <WalletButton wrapperProps={{ rounded: '2xl' }} />
          </Box>
        )}
        styles={{ height: '400px' }}
        text={t('messages.connect-wallet-inventory')}
      />
    )
  }

  if (isError) {
    return <NoResultsFallback styles={{ height: '400px' }} text={t('messages.error-boxes')} />
  }

  if (isLoading) {
    return (
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} rowGap={8} columnGap={6}>
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
        <Skeleton h={'323px'} rounded={'2xl'} startColor="ninja.500" endColor="ninja.600" />
      </SimpleGrid>
    )
  }

  if (!data?.length) {
    return (
      <NoResultsFallback
        render={() => (
          <Flex flexDir={{ base: 'column', sm: 'row' }} alignItems={'center'} gap={3} mt={6}>
            <Link href={'/lootbox'}>
              <Button rounded={'2xl'}>{t('lootbox.see-collections')}</Button>
            </Link>
          </Flex>
        )}
        styles={{ height: '400px' }}
        icon={<Icon boxSize={16} as={WalletIcon} />}
        text={t('lootbox.inventory-empty-description')}
      />
    )
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 3, xl: 4 }} rowGap={8} columnGap={6}>
      {data.map((lootbox) => (
        <LootboxCard key={lootbox.contract} lootbox={lootbox} />
      ))}
    </SimpleGrid>
  )
}

const LootboxCard = ({ lootbox }: { lootbox: Lootbox & { balance: string } }) => {
  const { t } = useTranslation()
  const { getLootboxImagePrefix } = useLootboxContext()

  if (lootbox.type === LootboxType.UNLIMITED) {
    return null
  }

  return (
    <Box rounded={'2xl'} bg={'ninja.600'}>
      <Flex
        onClick={() => router.push('/lootbox/' + lootbox.contract)}
        cursor={'pointer'}
        boxShadow={`0px 0px 15px 0px ${lootbox.image.shadowColor} inset, 0px 0px 15px 0px ${lootbox.image.shadowColor} inset, 0px -67px 100px 0px ${lootbox.image.shadowColor} inset;`}
        rounded={'2xl'}
        justifyContent={'center'}
        alignItems={'center'}
        h={'200px'}
        pos={'relative'}
        overflow={'hidden'}
        pt={10}
      >
        <Box pos={'absolute'} top={3} left={3}>
          <LootboxTags tags={lootbox.tags} />
        </Box>
        {lootbox.state !== LootboxState.ACTIVE && (
          <Box
            pos={'absolute'}
            boxSize={6}
            p={1}
            border={'1px solid'}
            borderColor={'whiteAlpha.300'}
            rounded={'lg'}
            top={4}
            right={3}
          >
            <LockIcon />
          </Box>
        )}

        <Box w={'130px'}>
          <Image
            width={150}
            height={144}
            src={getLootboxImagePrefix(lootbox.contract) + (lootbox.image.src || 'lootbox.png')}
            alt=""
          />
        </Box>
        <Flex
          fontWeight={700}
          bg={'white'}
          px={1}
          color={'ninja.500'}
          pos={'absolute'}
          bottom={0}
          right={0}
          fontSize={'xs'}
          rounded={'xl'}
          w={8}
          h={8}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {new BigNumberInWei(lootbox.balance).gt(99) ? '99+' : lootbox.balance}
        </Flex>
      </Flex>
      <Box px={5} py={4}>
        <Text fontSize={'lg'} fontWeight={700} mb={3}>
          {lootbox.title}
        </Text>
        <Button
          isDisabled={lootbox.state !== LootboxState.ACTIVE}
          w={'full'}
          onClick={() => router.push('/lootbox/' + lootbox.contract)}
          rounded={'2xl'}
          h={'52px'}
          px={10}
          display={'flex'}
          gap={2}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {lootbox.state !== LootboxState.ACTIVE && <LockIcon width={'25px'} />}
          {t('common.open')}
        </Button>
      </Box>
    </Box>
  )
}
